import { createAction, createPayloadAction, createRequestActionCreators, RequestAction } from 'helpers/actionsHelpers'
import { PayloadAction, SimpleAction } from 'interfaces/action'
import { Device, DevicesDataState, PasswordStrengthPayload } from 'interfaces/auth'
import { LOGIN_URL } from 'tangle-shared'

// ===========================================================================//
//                                LOGIN / LOGOUT                             //
// ===========================================================================//

type LoginFrom = 'login' | 'register' | 'session-continue'
export const LOGGED_IN = 'LOGGED_IN' as const
type LoggedIn = PayloadAction<typeof LOGGED_IN, { from: LoginFrom }>
export const loggedInActionCreator = (from: LoginFrom): LoggedIn =>
  createPayloadAction(LOGGED_IN, { from })

type LogoutPayload = {
  to: string
  redirect: boolean
}
const DEFAULT_LOGOUT_PAYLOAD: LogoutPayload = {
  to: LOGIN_URL,
  redirect: true,
}
export const LOGOUT = 'LOGOUT' as const
type LogOut = PayloadAction<typeof LOGOUT, LogoutPayload>
export const logOutActionCreator = (options?: Partial<LogoutPayload>): LogOut =>
  createPayloadAction(LOGOUT, {
    ...DEFAULT_LOGOUT_PAYLOAD,
    ...options,
  })

export const LOGGED_OUT = 'LOGGED_OUT' as const
type LoggedOut = SimpleAction<typeof LOGGED_OUT>
export const loggedOutActionCreator = () =>
  createAction(LOGGED_OUT)

// ===========================================================================//
//                               DEVICE MANAGEMENT                            //
// ===========================================================================//

export const GET_DEVICES = 'GET_DEVICES' as const
type GetDevices = SimpleAction<typeof GET_DEVICES>
export const getDevicesActionCreator = (): GetDevices =>
  createAction(GET_DEVICES)

export const SET_DEVICES = 'SET_DEVICES' as const
type SetDevices = PayloadAction<typeof SET_DEVICES, DevicesDataState>
export const setDevicesActionCreator = (devices: DevicesDataState): SetDevices =>
  createPayloadAction(SET_DEVICES, devices)

export const DELETE_DEVICE = 'DELETE_DEVICE' as const
type DeleteDevice = PayloadAction<typeof DELETE_DEVICE, number>
export const deleteDeviceActionCreator = (id: number): DeleteDevice =>
  createPayloadAction(DELETE_DEVICE, id)

export const FAIL_DELETE_DEVICE = 'FAIL_DELETE_DEVICE' as const
type FailDeleteDevice = PayloadAction<typeof FAIL_DELETE_DEVICE, Device>
export const failDeleteDeviceActionCreator = (device: Device): FailDeleteDevice =>
  createPayloadAction(FAIL_DELETE_DEVICE, device)

export const SET_DEVICE_UNVERIFIED = 'SET_DEVICE_UNVERIFIED' as const
type SetDeviceUnverified = SimpleAction<typeof SET_DEVICE_UNVERIFIED>
export const setDeviceUnverifiedActionCreator = () =>
  createAction(SET_DEVICE_UNVERIFIED)

export const SET_DEVICE_VERIFIED = 'SET_DEVICE_VERIFIED' as const
type SetDeviceVerified = SimpleAction<typeof SET_DEVICE_VERIFIED>
export const setDeviceVerifiedActionCreator = () =>
  createAction(SET_DEVICE_VERIFIED)

// ===========================================================================//
//                                   PASSWORDS                                //
// ===========================================================================//

export const GET_PASSWORD_STRENGTH = 'GET_PASSWORD_STRENGTH' as const
type GetPasswordStrength = RequestAction<typeof GET_PASSWORD_STRENGTH, string, PasswordStrengthPayload>
export const getPasswordStrengthActionCreator = createRequestActionCreators<typeof GET_PASSWORD_STRENGTH, string>(GET_PASSWORD_STRENGTH)

export const RESET_PASSWORD_STRENGTH = 'RESET_PASSWORD_STRENGTH' as const
type ResetPasswordStrength = SimpleAction<typeof RESET_PASSWORD_STRENGTH>
export const resetPasswordStrengthActionCreator = () => createAction(RESET_PASSWORD_STRENGTH)

export const TOGGLE_PASSWORD_RESET_CODE_SENT = 'TOGGLE_PASSWORD_RESET_CODE_SENT' as const
type TogglePasswordResetCodeSent = PayloadAction<typeof TOGGLE_PASSWORD_RESET_CODE_SENT, boolean>
export const togglePasswordResetCodeSentActionCreator = (payload: boolean) =>
  createPayloadAction(TOGGLE_PASSWORD_RESET_CODE_SENT, payload)


export type AuthActions =
  | LoggedIn
  | GetDevices
  | SetDevices
  | DeleteDevice
  | FailDeleteDevice
  | SetDeviceUnverified
  | SetDeviceVerified
  | LogOut
  | LoggedOut
  | GetPasswordStrength
  | ResetPasswordStrength
  | TogglePasswordResetCodeSent

export default AuthActions
